import React, { useEffect } from "react";
import { DragDropContext } from "react-beautiful-dnd";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import {
  FastBackwardOutlined,
  FastForwardOutlined,
  StepBackwardOutlined,
  StepForwardOutlined
} from "@ant-design/icons";

import { reorderList } from "../../utils/sort";
import { useDispatch, useSelector } from "react-redux";
import { selectSite } from "../../store/selector";
import { Button, notification, Row, Space, Typography } from "antd";
import { GRAY_400 } from "../../constant/colors";
import { fontWeights, sizes } from "../../constant/styles";
import { DATE_DATA_FORMAT, DATE_FORMAT_2 } from "../../utils/date";
import ACTIONS from "../../store/action";
import { DroppableDay } from "./DroppableDay";

const { Title } = Typography;

const OrdersWeeklyCalendarHeader = ({
  firstDayOfWeek,
  lastDayOfWeek,
  onClickWeekBackward,
  onClickWeekFastBackward,
  onClickWeekFastForward,
  onClickWeekForward,
  onClickToday,
  isFetching,
  filtersVisible
}) => {
  const { t } = useTranslation();

  return (
    <Row
      justify="space-between"
      gutter={[0, 16]}
      style={{ padding: filtersVisible ? "20px 40px 20px 30px" : "20px 40px 20px 35px" }}
    >
      <Title level={4}>{`${firstDayOfWeek.format("MMM D")} - ${lastDayOfWeek.format(DATE_FORMAT_2)}`}</Title>
      <Space id="cv-time-bar">
        <Button
          disabled={isFetching}
          ghost={true}
          onClick={onClickWeekFastBackward}
          icon={<FastBackwardOutlined style={{ fontSize: 20 }} />}
        />
        <Button
          disabled={isFetching}
          ghost={true}
          onClick={onClickWeekBackward}
          icon={<StepBackwardOutlined style={{ fontSize: 20 }} />}
        />
        <Button disabled={isFetching} ghost={true} onClick={onClickToday}>
          {t("today")}
        </Button>
        <Button
          disabled={isFetching}
          ghost={true}
          onClick={onClickWeekForward}
          icon={<StepForwardOutlined style={{ fontSize: 20 }} />}
        />
        <Button
          disabled={isFetching}
          ghost={true}
          onClick={onClickWeekFastForward}
          icon={<FastForwardOutlined style={{ fontSize: 20 }} />}
        />
      </Space>
    </Row>
  );
};

const WeeklyCalendarView = ({
  patients,
  weekOrdersGroupByDays,
  setWeekOrdersGroupByDays,
  firstDayOfWeek,
  setFirstDayOfWeek,
  lastDayOfWeek,
  onClickCalendar,
  isFetching,
  filtersVisible,
  isSavedListView
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const site = useSelector(selectSite);

  useEffect(() => {
    const range = moment().range(firstDayOfWeek, lastDayOfWeek);
    const weekOrders = patients.filter((p) => range.contains(moment(p.currentOrder.orderDate)));
    const weekOrdersGroupByDays = Array.from({ length: 7 }, (v, k) => k).map((k) =>
      weekOrders.filter((o) => moment(o.currentOrder.orderDate).diff(firstDayOfWeek, "days") === k)
    );
    setWeekOrdersGroupByDays(weekOrdersGroupByDays);
  }, [patients]);

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
      if (source.index === destination.index) return;
      const items = reorderList(weekOrdersGroupByDays[source.droppableId], source.index, destination.index);
      const newWeekOrdersGroupByDays = Array.from(weekOrdersGroupByDays);
      newWeekOrdersGroupByDays[source.droppableId] = items;
      setWeekOrdersGroupByDays(newWeekOrdersGroupByDays);
    } else {
      const patient = weekOrdersGroupByDays[source.droppableId][source.index];
      const sourceDate = moment(firstDayOfWeek).add(source.droppableId, "days");
      const destinationDate = moment(firstDayOfWeek).add(destination.droppableId, "days");

      const diff = destinationDate.diff(sourceDate, "days");
      const orderDate = moment(patient.currentOrder.orderDate);
      const newOrderDate = orderDate.add(diff, "days");
      const newPatient = {
        id: patient.id,
        pharmacyPatient: patient.id,
        orderDate: newOrderDate.toISOString()
      };
      const newCurrentOrder = {
        id: patient.currentOrder.id,
        orderDate: newOrderDate.toISOString()
      };

      notification.open({
        message: t("orders_calender.order_table_changed"),
        description: t("orders_calender.order_date_changed", {
          formDate: moment(patient.currentOrder.orderDate).format(DATE_DATA_FORMAT),
          toDate: newOrderDate.format(DATE_DATA_FORMAT)
        })
      });

      const newWeekOrdersGroupByDays = Array.from(weekOrdersGroupByDays);
      newWeekOrdersGroupByDays[destination.droppableId][destination.index] = {
        ...patient,
        currentOrder: { ...patient.currentOrder, orderDate: newPatient.orderDate }
      };
      newWeekOrdersGroupByDays[source.droppableId].splice(source.index, 1);
      setWeekOrdersGroupByDays(newWeekOrdersGroupByDays);
      dispatch(ACTIONS.bulkChangeAction(newPatient, [newCurrentOrder]));
    }
  };

  const onClickToday = () => setFirstDayOfWeek(moment().startOf("week"));
  const onClickWeekBackward = () => setFirstDayOfWeek(moment(firstDayOfWeek).subtract(1, "weeks"));
  const onClickWeekFastBackward = () => setFirstDayOfWeek(moment(firstDayOfWeek).subtract(4, "weeks"));
  const onClickWeekForward = () => setFirstDayOfWeek(moment(firstDayOfWeek).add(1, "weeks"));
  const onClickWeekFastForward = () => setFirstDayOfWeek(moment(firstDayOfWeek).add(4, "weeks"));

  return (
    <>
      <OrdersWeeklyCalendarHeader
        firstDayOfWeek={firstDayOfWeek}
        lastDayOfWeek={moment(firstDayOfWeek).add(6, "days")}
        onClickToday={onClickToday}
        onClickWeekBackward={onClickWeekBackward}
        onClickWeekFastBackward={onClickWeekFastBackward}
        onClickWeekForward={onClickWeekForward}
        onClickWeekFastForward={onClickWeekFastForward}
        isFetching={isFetching}
        filtersVisible={filtersVisible}
      />
      <div
        style={{
          height: isSavedListView ? "calc(100vh - 380px)" : "calc(100vh - 365px)",
          width: filtersVisible ? "calc(100vw - 370px)" : "calc(100vw - 50px)",
          overflowX: "auto",
          paddingRight: filtersVisible ? "15px" : "20px",
          marginLeft: filtersVisible ? "30px" : "20px"
        }}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gridGap: "0.5rem",
              overflowY: "auto"
            }}
          >
            {weekOrdersGroupByDays.map((dailyOrders, dayIndex) => {
              const date = moment(firstDayOfWeek).add(dayIndex, "days");
              return (
                <Space key={dayIndex} direction="vertical" style={{ alignItems: "center" }}>
                  {dayIndex <= 6 && (
                    <Title
                      level={5}
                      style={{
                        textAlign: "center",
                        color: GRAY_400,
                        fontWeight: fontWeights.semibold,
                        fontSize: sizes.medium,
                        textTransform: "uppercase"
                      }}
                    >
                      {date.format("ddd")}
                    </Title>
                  )}
                  <Title level={5} style={{ textAlign: "center" }}>
                    {date.format("D")}
                  </Title>
                  <DroppableDay
                    key={`${dayIndex}-${dailyOrders.length}`}
                    dailyOrders={dailyOrders}
                    dayIndex={dayIndex}
                    onClickCalendar={onClickCalendar}
                    mark={date.format(DATE_DATA_FORMAT) === moment().format(DATE_DATA_FORMAT)}
                    site={site}
                  />
                </Space>
              );
            })}
          </div>
        </DragDropContext>
      </div>
    </>
  );
};

export { WeeklyCalendarView };
